import React from 'react';
import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';

import { Box, Chip, CircularProgress, Button, TextField, IconButton, Typography, FormControlLabel, Switch, Tooltip } from "@mui/material";

import { RootState, useAppDispatch, useAppSelector } from "../../../state/state.tsx";
import { closeModal, toggleLoading } from "../../../state/ModalSlice/ModalSlice.ts";
import { useBackend } from "../../../hooks/backend/useBackend.ts";
import { toast } from "react-toastify";
import { UserStateV2 } from "../../Subscriptions/types/UserStateV2.ts";
import { Timestamp } from "firebase/firestore";
import CloseIcon from "@mui/icons-material/Close";

import { getDownloadURL, ref, uploadBytes, deleteObject } from "firebase/storage";
import { storage } from '../../../Config/firebase';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';

import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined';


export default function EditProfileModal() {

    // ------------------------DialogHandlers
    const Dispatch = useAppDispatch();
    const open = useAppSelector((state: RootState) => state.ModalSlice.editServiceProviderModalOpen);
    const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading)
    const handleClose = () => {
        Dispatch(closeModal("editServiceProviderModalOpen"))
        setModalError({
            serviceProviderName: false, email: false, serviceType: false
        })

    };
    const ServiceProviderId = useAppSelector(selectEffectiveUID) 

    // ------------------------Form States

    const [profilePhoto, setProfilePhoto] = useState<string>('https://firebasestorage.googleapis.com/v0/b/coachprodev.appspot.com/o/Config%2FProfilePhoto2Update.png?alt=media&token=53ea668c-04f3-493a-911d-15eba1ce897c');
    const [serviceProviderName, setServiceProviderName] = useState<string>("")
    const [email, setEmail] = useState<string>("")
    const [, setIsError] = React.useState<boolean>(false)
    const [modalError, setModalError] = React.useState({
        serviceProviderName: false, email: false, serviceType: false
    });
    const [selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>([]);
    const [allowSubscriberToPause, setAllowSubscriberToPause] = useState<boolean>(false)

    // Add new state for multiple branding images
    const [brandingImages, setBrandingImages] = useState<{
        file: File;
        preview: string;
    }[]>([]);

    // Add new state for business description
    const [businessDescription, setBusinessDescription] = useState<string>("");

    // Add new state for GSTIN
    const [gstin, setGstin] = useState<string>("");
    const [gstinError, setGstinError] = useState<boolean>(false);

    // Add new state for month boundary
    const [mmAlign2monthBoundary, setmmAlign2monthBoundary] = useState<boolean>(false);

    // Add new state for unmasking customer numbers
    const [unmaskCustomerNumbers, setUnmaskCustomerNumbers] = useState<boolean>(false);

    //-------------------------Options

    const ServiceTypes = [
        "Preschool",
        "Creche",
        "Tuition",
        "PGHostels",
        "Rentals",
        "CableTV",
        "Sports",
        "Fitness",
        "Other"
    ]
    // -----------------------Fetch states from Store

    const userDataV2: UserStateV2 | null = useAppSelector((state: RootState) => state.userSliceV2.data)

    useEffect(() => {
        if (userDataV2) {
            setEmail(userDataV2?.email)
            setServiceProviderName(userDataV2?.display_name)
            setProfilePhoto(userDataV2?.photo_url)
            setSelectedServiceTypes(Array.isArray(userDataV2.ServiceTypes) ? userDataV2.ServiceTypes : [])
            if (userDataV2.GSTIN) {
                setGstin(userDataV2.GSTIN)
            }
            if (userDataV2.plundrDescription) {
                setBusinessDescription(userDataV2.plundrDescription)
            }
            if (userDataV2.plundrImages && Array.isArray(userDataV2.plundrImages)) {
                setBrandingImages(userDataV2.plundrImages.map(url => ({
                    file: new File([], "existing-image"), // placeholder file
                    preview: url
                })));
            }

            setAllowSubscriberToPause(userDataV2.pauseBySubscriber||false)
            setmmAlign2monthBoundary(userDataV2.mmAlign2monthBoundary||false)
            
            // Fix the state initialization for unmaskCustomerNumbers
            // We need to handle all possible cases explicitly
            if (userDataV2.unmaskCustomerNumbers === true) {
                setUnmaskCustomerNumbers(true);
            } else {
                setUnmaskCustomerNumbers(false);
            }
            
            
        }
    }, [userDataV2]);


    // ------------------------Handle Change Form States

    const handleChangeServiceProviderName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setServiceProviderName(event.target.value)
    }

    const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value)
    }

    const { uploadFileInFirestore } = useBackend("")

    async function handlePhotoChange(e: any) {
        const file = e.target.files[0];

        const url: string = await uploadFileInFirestore(ServiceProviderId || '', file)
        setProfilePhoto(url)
    }

    const handleChangeServiceType = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {
            target: { value },
        } = event;
        setSelectedServiceTypes(
            // On autofill we get a stringified value.
            typeof value === 'string' ? value.split(',') : value,
        );
    };


    // Add after other handle functions
    const handleBrandingImagesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files) return;

        // Convert FileList to Array and process each file
        const newFiles = Array.from(files).slice(0, 6 - brandingImages.length); // Limit to remaining slots

        newFiles.forEach(file => {
            if (file.size > 5 * 1024 * 1024) {
                toast.error(`File ${file.name} is too large. Maximum size is 5MB`);
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setBrandingImages(prev => [...prev, {
                    file,
                    preview: reader.result as string
                }]);
            };
            reader.readAsDataURL(file);
        });
    };

    const removeBrandingImage = async (index: number) => {
        const imageToRemove = brandingImages[index];

        // If this is an existing image (has a URL from Firebase)
        if (imageToRemove.preview.startsWith('https://firebasestorage.googleapis.com')) {
            try {
                // Create a reference to the file to delete
                const imageRef = ref(storage, imageToRemove.preview);
                await deleteObject(imageRef);
            } catch (error) {
                console.error("Error deleting image from storage:", error);
                toast.error("Failed to delete image from storage");
            }
        }

        setBrandingImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleChangeAllowSubscriberToPause = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAllowSubscriberToPause(event.target.checked)
    }

    const handleGstinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.toUpperCase();
        setGstin(value);
    };

    // Add handler for month boundary toggle
    const handleChangeMonthBoundary = (event: React.ChangeEvent<HTMLInputElement>) => {
        setmmAlign2monthBoundary(event.target.checked)
    }

    // Add handler for unmasking customer numbers toggle
    const handleChangeUnmaskCustomerNumbers = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.checked;
        
        setUnmaskCustomerNumbers(newValue);
    }



    // -------------------------------------Error Handlers For Modal


    function CheckErrors(): boolean {
        setIsError(false)
        let localerror: boolean = false

        if (serviceProviderName.trim() === "") {
            setIsError(true)
            localerror = true
            setModalError((prev) => {
                return { ...prev, serviceProviderName: true }
            })
        } else {
            setModalError((prev) => {
                return { ...prev, serviceProviderName: false }
            })
        }

        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (email.trim() === "" || (emailPattern && !emailPattern.test(email))) {
            setIsError(true)
            localerror = true
            setModalError((prev) => {
                return { ...prev, email: true }
            })
        } else {
            setModalError((prev) => {
                return { ...prev, email: false }
            })
        }

        if (selectedServiceTypes.length === 0) {
            setIsError(true)
            localerror = true
            setModalError((prev) => {
                return { ...prev, serviceType: true }
            })
        } else {
            setModalError((prev) => {
                return { ...prev, serviceType: false }
            })
        }

        // GSTIN validation (only if a value is provided)
        if (gstin.trim() !== "") {
            const gstinRegex = /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}Z[0-9A-Za-z]{1}$/;
            if (!gstinRegex.test(gstin)) {
                setIsError(true)
                localerror = true
                setGstinError(true)
            } else {
                setGstinError(false)
            }
        } else {
            setGstinError(false)
        }

        return localerror
    }

    // Add upload function for branding images
    const handleBrandingImagesUpload = async (): Promise<string[]> => {
        const uploadedUrls: string[] = [];

        for (const image of brandingImages) {
            try {
                // If the image is already a Firebase URL, keep it as is
                if (image.preview.startsWith('https://firebasestorage.googleapis.com')) {
                    uploadedUrls.push(image.preview);
                    continue;
                }

                // Only upload new images
                const timestamp = Date.now();
                const storageRef = ref(storage, `plundrImages/${ServiceProviderId || ''}/${timestamp}_${image.file.name}`);
                await uploadBytes(storageRef, image.file);
                const url = await getDownloadURL(storageRef);
                uploadedUrls.push(url);
            } catch (error) {
                console.error("Error uploading image:", error);
                toast.error(`Failed to upload image ${image.file.name}`);
            }
        }

        return uploadedUrls;
    };

    // --------------------------On Submit Operation

    const { update } = useBackend(`/ServiceProviders`)

    async function onSubmit(e: any) {
        Dispatch(toggleLoading(true))
        e.preventDefault()
        const Localerrors = CheckErrors()

        if (Localerrors) {
            Dispatch(toggleLoading(false))
            return
        }

        try {
            // Upload branding images and get URLs
            const brandingImageUrls = await handleBrandingImagesUpload();
            
         

            const response = await update(ServiceProviderId || '', {
                display_name: serviceProviderName,
                email: email,
                edited_time: Timestamp.fromDate(new Date()),
                photo_url: profilePhoto,
                ServiceTypes: selectedServiceTypes,
                plundrDescription: businessDescription,
                plundrImages: brandingImageUrls,
                pauseBySubscriber: allowSubscriberToPause,
                GSTIN: gstin || null,
                mmAlign2monthBoundary: mmAlign2monthBoundary,
                unmaskCustomerNumbers: unmaskCustomerNumbers,
            })

            handleClose()
            Dispatch(toggleLoading(false))
            if (response === "error") {
                return
            }
            toast.success("Profile updated Successfully!")
        } catch (error) {
            console.error("Error updating profile:", error);
            toast.error("Failed to update profile. Please try again.");
            Dispatch(toggleLoading(false));
        }
    }


    return (<React.Fragment>

        <Dialog
            sx={{
                padding: 0,
                '& .MuiPaper-root': {
                    borderRadius: '10px',
                },
            }}
            maxWidth="md"
            open={open}
            PaperProps={{
                component: 'div',
            }}
        >

            <div className={"absolute right-4 top-4 "}><IconButton onClick={handleClose}>
                <CloseIcon />
            </IconButton></div>

            <DialogContent sx={{ paddingX: "60px", paddingY: "50px" }} className={"bg-white"}>
                <div className={"header pb-[20px]"}>
                    <p className={"text-cf_heading font-[600] "}>Edit Profile</p>
                    {/*<p className={"text-cf_small font-[400] "}>Add New Subscribers to {subscriptionName}</p>*/}
                </div>
                <div className={"pt-0"}>
                    <div className={""}>
                        <div className={""}>
                            <Box
                                component={"div"}
                                sx={{}}
                            >
                                <div className="flex flex-cl justify-cente w-full items-center gap-5">
                                    <img
                                        className="inline-block size-24 rounded-full shadow-lg ring-offset-2 ring-2 ring-cc_modal-grey-border "
                                        src={profilePhoto}
                                        alt="Profile" />
                                    <div>
                                        <div>
                                            <input
                                                type="file"
                                                id="upload-photo"
                                                accept="image/*"
                                                style={{ display: 'none' }}
                                                onChange={handlePhotoChange}
                                            />
                                            <label htmlFor="upload-photo"
                                                className="py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none cursor-pointer">
                                                <svg className="flex-shrink-0 size-4"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="20" height="20" viewBox="0 0 24 24" fill="none"
                                                    stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                                    strokeLinejoin="round">
                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                    <polyline points="17 8 12 3 7 8" />
                                                    <line x1="12" x2="12" y1="3" y2="15" />
                                                </svg>
                                                Upload Business Logo
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className={"TextFields flex flex-col gap-4 mt-8"}>
                                    <div className={"flex  flex-col gap-4 mb-4"}>
                                        <TextField
                                            onChange={handleChangeServiceProviderName}
                                            value={serviceProviderName}
                                            size={"small"}
                                            required={true}
                                            fullWidth={true}
                                            id="Name"
                                            label="Name"
                                            error={modalError.serviceProviderName}
                                            helperText={modalError.serviceProviderName ? " Name  is required" : ""}
                                            variant="outlined"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey', borderRadius: '10px'// Default border color
                                                    }, '&:hover fieldset': {
                                                        borderColor: 'grey', // Border color on hover
                                                    }, '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue', // Border color when focused
                                                    }, '& .MuiInputBase-input': {
                                                        fontSize: '14px', // Setting font size
                                                    },
                                                }, fontWeight: 400, width: '550px'
                                            }}
                                        />

                                        <TextField
                                            onChange={handleChangeEmail}
                                            value={email}
                                            size={"small"}
                                            required={true}
                                            id="Email"
                                            label="Email"
                                            fullWidth={true}
                                            error={modalError.email}
                                            helperText={modalError.email ? "Incorrect email address" : ""}
                                            variant="outlined"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey', borderRadius: '10px'// Default border color
                                                    }, '&:hover fieldset': {
                                                        borderColor: 'grey', // Border color on hover
                                                    }, '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue', // Border color when focused
                                                    }, '& .MuiInputBase-input': {
                                                        fontSize: '14px', // Setting font size
                                                    },
                                                }, fontWeight: 400, width: '550px'
                                            }}
                                        />

                                        <TextField
                                            onChange={handleGstinChange}
                                            value={gstin}
                                            size={"small"}
                                            id="GSTIN"
                                            label="GSTIN (Optional)"
                                            fullWidth={true}
                                            error={gstinError}
                                            helperText={gstinError ? "Invalid GSTIN format" : ""}
                                            variant="outlined"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px'
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px',
                                                    },
                                                },
                                                fontWeight: 400,
                                                width: '550px'
                                            }}
                                        />

                                        <Box sx={{ width: '550px' }}>
                                            <Typography variant="body2" color={modalError.serviceType ? "error" : "text.secondary"} mb={1}>
                                                Service Types {modalError.serviceType && "- At least one service type must be selected"}
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {ServiceTypes.map((type) => (
                                                    <Chip
                                                        key={type}
                                                        label={type}
                                                        onClick={() => {
                                                            setSelectedServiceTypes(prev => {
                                                                const currentTypes = Array.isArray(prev) ? prev : [];
                                                                if (currentTypes.includes(type)) {
                                                                    if (currentTypes.length === 1) return currentTypes;
                                                                    return currentTypes.filter(t => t !== type);
                                                                } else {
                                                                    return [...currentTypes, type];
                                                                }
                                                            });
                                                        }}
                                                        color={Array.isArray(selectedServiceTypes) && selectedServiceTypes.includes(type) ? "primary" : "default"}
                                                        variant={Array.isArray(selectedServiceTypes) && selectedServiceTypes.includes(type) ? "filled" : "outlined"}
                                                        sx={{
                                                            '&:hover': {
                                                                backgroundColor: Array.isArray(selectedServiceTypes) && selectedServiceTypes.includes(type)
                                                                    ? 'primary.dark'
                                                                    : 'action.hover'
                                                            }
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>


                                        <div className="flex flex-col gap-4">
                                            {/* <div className={"text-lg flex items-center"}>
                                                <FormControlLabel
                                                    label={"Allow Customers To Pause?"}
                                                    labelPlacement="start"
                                                    sx={{ marginLeft: '0' }} 
                                                    control={
                                                        <Switch
                                                            checked={allowSubscriberToPause}
                                                            onChange={handleChangeAllowSubscriberToPause}
                                                            inputProps={{ 'aria-label': 'controlled' }}
                                                        />
                                                    }
                                                />
                                                <Tooltip title="Enabling this will allow users to submit pause requests via WhatsApp. You can review and approve these requests." arrow>
                                                    <HelpOutlinedIcon fontSize="small" className="ml-2 text-gray-500 cursor-help" />
                                                </Tooltip>
                                            </div> */}

                                            {/* <div className={"text-lg flex items-center "}>
                                                <FormControlLabel
                                                    label={"Align Subscriptions to Month End?"}
                                                    labelPlacement="start"
                                                    sx={{ marginLeft: '0' }} 
                                                    control={
                                                        <Switch
                                                            checked={mmAlign2monthBoundary}
                                                            onChange={handleChangeMonthBoundary}
                                                            inputProps={{ 'aria-label': 'controlled' }}
                                                        />
                                                    }
                                                />
                                                <Tooltip title="When enabled, all multi-month subscriptions will end on the last day of their respective months (e.g. plan ending March 31, June 30, etc.) - Only applicable for new subscriptions" arrow>
                                                    <HelpOutlinedIcon fontSize="small" className="ml-2 text-gray-500 cursor-help" />
                                                </Tooltip>
                                            </div> */}
                                            
                                            <div className={"text-lg flex items-center "}>
                                                <FormControlLabel
                                                    label={"Unmask Customer Numbers in Export Data?"}
                                                    labelPlacement="start"
                                                    sx={{ marginLeft: '0' }} 
                                                    control={
                                                        <Switch
                                                            checked={unmaskCustomerNumbers}
                                                            onChange={handleChangeUnmaskCustomerNumbers}
                                                            inputProps={{ 'aria-label': 'controlled' }}
                                                        />
                                                    }
                                                />
                                                <Tooltip title="When enabled, customer phone numbers will be shown in full in exported data. Otherwise, numbers will be partially masked for privacy." arrow>
                                                    <HelpOutlinedIcon fontSize="small" className="ml-2 text-gray-500 cursor-help" />
                                                </Tooltip>
                                            </div>
                                        </div>


                                        <div><p className='font-semibold'>The below fields will be visible in your page in plundr.</p></div>
                                        <TextField
                                            label="Description of your business"
                                            multiline
                                            rows={3}
                                            value={businessDescription}
                                            onChange={(e) => setBusinessDescription(e.target.value)}
                                            fullWidth
                                            placeholder="Tell us about your business..."
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px'
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px',
                                                    },
                                                },
                                                fontWeight: 400,
                                                mb: 2,
                                                width: '550px'
                                            }}
                                        />

                                        <div>
                                            <Button
                                                variant="outlined"
                                                component="label"
                                                fullWidth
                                                disabled={brandingImages.length >= 6}
                                                sx={{
                                                    mb: 0,
                                                    borderRadius: '8px',
                                                    textTransform: 'none',
                                                    color: '#1D4ED8',
                                                    borderColor: '#1D4ED8',
                                                    '&:hover': {
                                                        backgroundColor: '#1D4ED8',
                                                        color: "#FFFFFF"
                                                    },
                                                }}
                                            >
                                                Upload Branding Images <span className={"text-sm ml-2"}>(Max 5mb each, up to 6 images)</span>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    hidden
                                                    multiple
                                                    onChange={handleBrandingImagesChange}
                                                />
                                            </Button>
                                            <p className={"text-xs mt-2.5 text-center"}>Preferred size 1600px * 1600px</p>
                                        </div>

                                        {brandingImages.length > 0 && (
                                            <div className="flex flex-wrap gap-4 mt-4" style={{ width: '550px' }}>
                                                {brandingImages.map((image, index) => (
                                                    <Box key={index} sx={{ position: 'relative', width: 100, height: 100 }}>
                                                        <img
                                                            src={image.preview}
                                                            alt={`Branding preview ${index + 1}`}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                                borderRadius: '8px'
                                                            }}
                                                        />
                                                        <IconButton
                                                            onClick={() => removeBrandingImage(index)}
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 4,
                                                                right: 4,
                                                                bgcolor: 'background.paper'
                                                            }}
                                                        >
                                                            <CloseIcon />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                            </div>
                                        )}



                                    </div>



                                </div>
                            </Box>
                        </div>
                    </div>


                </div>

                <div className={"flex gap-2 pt-4"}>
                    <button onClick={(e) => {
                        e.preventDefault();
                        handleClose()
                    }} disabled={isLoading}
                        className={`w-full bg-cc_bg-tertiary border border-solid border-gray-500 p-1.5 rounded-lg ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}>Cancel
                    </button>
                    <button
                        onClick={onSubmit} disabled={isLoading}
                        className={`w-full bg-cc_brand text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}>{isLoading &&
                            <CircularProgress sx={{ color: "white" }} size={20} />}
                        <p className={"ml-2"}>{isLoading ? "Loading" : "Save"}</p>
                    </button>

                </div>
            </DialogContent>

        </Dialog>

    </React.Fragment>);
}